import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { storageService } from '../services/storage';
import { STORAGE_CONFIG } from '../constants/storageConstants';
import { differenceInDays, parseISO, isValid, parse, differenceInSeconds, format } from 'date-fns';
import { shiftCompetitionDates } from '../utils/dateUtils';
import SHA256 from 'crypto-js/sha256';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import { competitionConfigsService } from '../services/supabase';
import CompetitionProgress from '../components/CompetitionProgress';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCompetitionRouting } from '../hooks/useCompetitionRouting';
import { updateCompetition, addDefaultRotations, setCurrentCompetition, fetchCompetition } from '../store/competitionSlice';
import { saveCompetitionVersion, loadCompetitionVersion } from '../store/competitionThunks';
import { Box, Container, Paper, Tab, Tabs, Typography, useTheme, Chip, Button, Snackbar, Alert, CircularProgress, TextField, MenuItem, Collapse, Tooltip, IconButton, ToggleButtonGroup, ToggleButton, Skeleton } from '@mui/material';
import { getStatusLabel, getStatusColor, COMPETITION_STATUSES } from '../constants/competitionConstants';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VersionHistoryDialog from '../components/VersionHistoryDialog';
import RevisionCompareDialog from '../components/RevisionCompareDialog';
import CompetitionSchedule from '../components/CompetitionSchedule';
import CompetitionJudging from '../components/CompetitionJudging';
import CompetitionStats from '../components/CompetitionStats';
import SessionDetails from '../components/SessionDetails';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MapIcon from '@mui/icons-material/Map';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useCompetitionImport } from '../hooks/useCompetitionImport';
import ImportModal from '../components/ImportModal';
import FacilityLayoutDesigner from '../components/FacilityLayoutDesigner';
import SessionDefinition from '../components/SessionDefinition';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';

// Lazy load heavy components for better initial load performance
const CompetitionTeams = React.lazy(() => import('../components/CompetitionTeams'));
const CompetitionResults = React.lazy(() => import('../components/CompetitionResults'));

const SANCTION_ORGS = [
  { value: 'USAG', label: 'USA Gymnastics' },
  { value: 'AAU', label: 'Amateur Athletic Union' },
  { value: 'NCAA', label: 'National Collegiate Athletic Association' },
  { value: 'OTHER', label: 'Other' }
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

const getTimezoneLabel = (value) => {
  const tz = TIMEZONES.find(t => t.value === value);
  return tz ? tz.label : value || 'Not specified';
};

const CompetitionDetails = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const layoutWidth = useSelector((state) => state.theme.layoutWidth);

  const competitions = useSelector(state => state?.competition?.competitions || []);
  const loading = useSelector(state => state?.competition?.loading || false);
  const currentCompetition = useSelector(state => state?.competition?.currentCompetition);
  const competitionWrapper = useSelector(state => {
    if (!state?.competition?.competitions || !id) return {
      competition: {},
      statistics: {},
      sessions: [],
      teams: [],
      gymnasts: [],
      levels: [],
      coaches: [],
      judges: [],
      ageGroups: [],
      events: [],
      rotations: []
    };
    
    const found = state.competition.competitions.find(c => 
      c?.competition?.id && String(c.competition.id) === String(id)
    );
    
    return found || {
      competition: {},
      statistics: {},
      sessions: [],
      teams: [],
      gymnasts: [],
      levels: [],
      coaches: [],
      judges: [],
      ageGroups: [],
      events: [],
      rotations: []
    };
  });
  
  // Use URL-based routing for tabs
  const { currentTab, navigateToTab, getQueryParam, updateQueryParams } = useCompetitionRouting();

  // Initialize state from URL params
  const [selectedSessionIndex, setSelectedSessionIndex] = React.useState(() => {
    const sessionParam = getQueryParam('session');
    // URL uses 1-based session numbers, convert to 0-based index
    return sessionParam ? parseInt(sessionParam, 10) - 1 : 0;
  });
  const [athleteTeamsView, setAthleteTeamsView] = useState(() => {
    return getQueryParam('view') || 'athletes';
  });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' });
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [addSessionDialogOpen, setAddSessionDialogOpen] = React.useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = React.useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedValues, setEditedValues] = React.useState({});
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [versionInfo, setVersionInfo] = useState({ currentRevision: null, totalVersions: 0, lastUpdated: null });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeroCollapsed, setIsHeroCollapsed] = useState(false);
  const [isDocking, setIsDocking] = useState(false);
  const [heroHeight, setHeroHeight] = useState(260); // default spacing to avoid jump before measure
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const fileInputRef = useRef(null);
  const heroRef = useRef(null);
  const autoScrollTriggered = useRef(false);

  // Destructure competition data early so hooks below can safely reference it
  // Use currentCompetition as primary source for data that changes frequently
  const {
    competition = {},
    sessions = [],
    levels = [],
    ageGroups = [],
    events = [],
    rotations = []
  } = competitionWrapper || {};

  // Use currentCompetition for frequently updated data to ensure immediate UI updates
  const statistics = currentCompetition?.statistics || competitionWrapper?.statistics || {};
  const teams = currentCompetition?.teams || competitionWrapper?.teams || [];
  const gymnasts = currentCompetition?.gymnasts || competitionWrapper?.gymnasts || [];
  const coaches = currentCompetition?.coaches || competitionWrapper?.coaches || [];
  const judges = currentCompetition?.judges || competitionWrapper?.judges || [];

  const handleLogoUpload = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      setUploadingLogo(true);
      
      // Create a unique file name using competition ID and timestamp
      const fileExt = file.name.split('.').pop();
      const fileName = `${competition.id}/logo-${Date.now()}.${fileExt}`;
      const filePath = `${STORAGE_CONFIG.COMPETITION_ASSETS.LOGOS}/${fileName}`;

      const { publicUrl } = await storageService.uploadFile(file, filePath);

      // Update edited values to track unsaved logo change
      setEditedValues({ ...editedValues, logo: publicUrl });
      setHasUnsavedChanges(true);
      
      setSnackbar({
        open: true,
        message: 'Logo uploaded - click Save to persist changes'
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading logo'
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const steps = [
    'Basic Information',
    'Session Structure', 
    'Competition Settings'
  ];

  const handleImport = useCompetitionImport(() => setImportModalOpen(false));

  const competitionColors = useMemo(() => {
    if (!competitionWrapper?.competition) {
      return { 
        primary: theme.palette.primary.main, 
        secondary: theme.palette.secondary.main 
      };
    }

    const competition = competitionWrapper.competition;

    if (competition.colors) {
      // Ensure colors are valid - fallback to theme if empty
      return {
        primary: competition.colors.primary || theme.palette.primary.main,
        secondary: competition.colors.secondary || theme.palette.secondary.main
      };
    }

    // Generate a random color if no colors specified
    const getRandomColor = () => {
      const hue = Math.floor(Math.random() * 360);
      const s = 70;
      const l = 50;

      const c = (1 - Math.abs(2 * l / 100 - 1)) * s / 100;
      const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
      const m = l / 100 - c / 2;

      let r, g, b;
      if (hue < 60) { [r, g, b] = [c, x, 0]; }
      else if (hue < 120) { [r, g, b] = [x, c, 0]; }
      else if (hue < 180) { [r, g, b] = [0, c, x]; }
      else if (hue < 240) { [r, g, b] = [0, x, c]; }
      else if (hue < 300) { [r, g, b] = [x, 0, c]; }
      else { [r, g, b] = [c, 0, x]; }

      const toHex = (n) => {
        const hex = Math.round((n + m) * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const primary = getRandomColor();
    const secondary = getRandomColor();

    const isValidHex = (color) => /^#[0-9A-F]{6}$/i.test(color);

    if (!isValidHex(primary) || !isValidHex(secondary)) {
      console.error('Invalid color format generated:', { primary, secondary });
      return {
        primary: '#2196f3',
        secondary: '#7C4DFF',
      };
    }

    return {
      primary,
      secondary,
    };
  }, [competitionWrapper?.competition, theme.palette.primary.main, theme.palette.secondary.main]);

  React.useEffect(() => {
    // If we have an ID but no current competition, try to fetch it
    if (id && !competitionWrapper?.competition?.id) {
      const competition = competitions.find(c => 
        c?.competition?.id && String(c.competition.id) === String(id)
      );
      if (competition) {
        dispatch(setCurrentCompetition(competition));
      } else {
        // Competition not in store, fetch it
        dispatch(fetchCompetition(id));
      }
    }
  }, [id, competitions, dispatch, competitionWrapper]);

  React.useEffect(() => {
    if (!competitionWrapper?.competition?.id) return;
    if (!currentCompetition?.competition?.id) {
      dispatch(setCurrentCompetition(competitionWrapper));
      return;
    }
    if (String(currentCompetition.competition.id) !== String(competitionWrapper.competition.id)) {
      dispatch(setCurrentCompetition(competitionWrapper));
    }
  }, [competitionWrapper, currentCompetition, dispatch]);

  // Load version info
  React.useEffect(() => {
    const loadVersionInfo = async () => {
      if (!competition?.id) return;
      
      try {
        const versions = await competitionConfigsService.getVersions(competition.id);
        const latestVersion = await competitionConfigsService.getLatestVersion(competition.id);
        
        setVersionInfo({
          currentRevision: latestVersion?.revision || 1,
          totalVersions: versions?.length || 0,
          lastUpdated: versions?.[0]?.created_at || null
        });
      } catch (error) {
        console.error('Error loading version info:', error);
      }
    };

    loadVersionInfo();
  }, [competition?.id]);

  React.useEffect(() => {
    if (competitionWrapper?.competition?.name) {
      document.title = `${competitionWrapper.competition.name} - Virtius NextGen`;
    } else {
      document.title = 'Virtius NextGen';
    }
    
    return () => {
      document.title = 'Virtius NextGen';
    };
  }, [competitionWrapper?.competition?.name]);

  const heroStorageKey = competition?.id ? `competitionHeroCollapsed:${competition.id}` : null;

  // Restore hero collapsed preference per-competition
  React.useEffect(() => {
    if (!heroStorageKey) return;
    try {
      const storedValue = localStorage.getItem(heroStorageKey);
      if (storedValue === 'true') {
        setIsHeroCollapsed(true);
      } else if (storedValue === 'false') {
        setIsHeroCollapsed(false);
      }
    } catch (error) {
      console.error('Error reading hero preference:', error);
    }
  }, [heroStorageKey]);

  // Persist hero collapsed preference per-competition
  React.useEffect(() => {
    if (!heroStorageKey) return;
    try {
      localStorage.setItem(heroStorageKey, isHeroCollapsed ? 'true' : 'false');
    } catch (error) {
      console.error('Error saving hero preference:', error);
    }
  }, [heroStorageKey, isHeroCollapsed]);

  // Measure hero height so we can preserve spacing when collapsed without jump
  React.useEffect(() => {
    if (!heroRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Only update if we get a valid height AND we are not currently collapsed
        // This prevents heroHeight from becoming 0 when we collapse it, 
        // preserving the value for scroll adjustments.
        if (entry.contentRect.height > 10 && !isHeroCollapsed) {
          setHeroHeight(entry.contentRect.height);
        }
      }
    });
    
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [competitionWrapper?.competition?.id, isEditing, editedValues, isHeroCollapsed]);

  // Handle scroll adjustments after docking
  const shouldAdjustScroll = useRef(false);
  React.useLayoutEffect(() => {
    if (shouldAdjustScroll.current && isHeroCollapsed) {
      shouldAdjustScroll.current = false;
      // Adjust scroll position by subtracting the hero height
      // This effectively "teleports" the viewport to the same content relative position
      // now that the hero is gone.
      window.scrollTo({
        top: window.scrollY - heroHeight,
        behavior: 'auto' // Instant jump to prevent visual glitch
      });
    }
  }, [isHeroCollapsed, heroHeight]);

  // Track scroll position for sticky header with smooth transitions
  React.useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY || document.documentElement.scrollTop;

          // Standard sticky header threshold
          const pastThreshold = scrollPosition > 150;
          setIsScrolled(pastThreshold);

          // DOCKING LOGIC (Long Pages):
          // If user scrolls completely past the hero, we "dock" it (collapse it).
          // We set isDocking=true to disable the collapse animation, allowing
          // the scroll adjustment to happen instantly and seamlessly.
          if (!isHeroCollapsed && heroHeight > 0 && scrollPosition > heroHeight + 20) {
             shouldAdjustScroll.current = true;
             setIsDocking(true);
             setIsHeroCollapsed(true);
          }
          
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHeroCollapsed, heroHeight]);

  // Handle "fake" scroll for short pages where native scroll won't trigger the threshold
  React.useEffect(() => {
    const handleWheel = (e) => {
      // Only act if expanded and not locked
      if (isHeroCollapsed || autoScrollTriggered.current) return;

      // Check if we are at the bottom or can't scroll further
      // (allow a small buffer for float inaccuracies)
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const isAtBottom = (window.scrollY >= maxScroll - 10);

      // If we are stuck at the bottom (or page is too short), 
      // treat wheel-down as a collapse trigger
      if (isAtBottom && e.deltaY > 20) {
        autoScrollTriggered.current = true;
        setIsHeroCollapsed(true);
        setTimeout(() => {
          autoScrollTriggered.current = false;
        }, 400);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [isHeroCollapsed]);

  // Track unsaved changes - only if values actually differ from original
  React.useEffect(() => {
    if (!isEditing) {
      setHasUnsavedChanges(false);
      return;
    }
    
    // Check if any edited value actually differs from the original
    const hasRealChanges = Object.keys(editedValues).some(key => {
      const editedValue = editedValues[key];
      let originalValue;
      
      // Handle nested properties like colors
      if (key === 'primaryColor') {
        originalValue = competition.colors?.primary ?? competitionColors.primary;
      } else if (key === 'secondaryColor') {
        originalValue = competition.colors?.secondary ?? competitionColors.secondary;
      } else {
        originalValue = competition[key];
      }
      
      // Compare values (handle null/undefined equivalence)
      return editedValue !== originalValue && 
             !(editedValue == null && originalValue == null);
    });
    
    // Tracking unsaved changes (debug logging removed)
    setHasUnsavedChanges(hasRealChanges);
  }, [editedValues, isEditing, competition, competitionColors]);

  // Warn on browser navigation/close if unsaved or unpublished changes
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges || hasUnpublishedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, hasUnpublishedChanges]);

  // Block browser back/forward navigation if unsaved or unpublished changes
  React.useEffect(() => {
    if (!hasUnsavedChanges && !hasUnpublishedChanges) return;

    const handlePopState = () => {
      if (!window.confirm('You have unsaved changes. Leave anyway?')) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, hasUnpublishedChanges]);

  const handleExport = () => {
    if (!competition || !competitionWrapper) return;

    const now = new Date();

    // Create a deterministic string for hashing
    const dataForHash = JSON.stringify({
      competition,
      sessions: sessions || [],
      gymnasts: gymnasts || [],
      levels: levels || [],
      teams: teams || [],
      judges: judges || [],
      coaches: coaches || [],
      ageGroups: ageGroups || [],
      events: events || [],
      rotations: rotations || [],
      statistics: statistics || {},
    }, Object.keys({}).sort());

    // Generate SHA-256 hash
    const checksum = SHA256(dataForHash).toString();

    const exportData = {
      competition,
      sessions,
      gymnasts,
      levels,
      teams,
      judges,
      coaches,
      ageGroups,
      events,
      rotations,
      statistics,
      metadata: {
        exportDate: now.toISOString(),
        checksum,
        version: '1.0',
        ...(competitionWrapper?.metadata?.usagLastModified && { usagLastModified: competitionWrapper.metadata.usagLastModified })
      }
    };
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/[/,:]/g, '-').replace(' ', '_');
    const fileName = `${competition.name.replace(/[^a-z0-9]/gi, '_')}_${timestamp}.json`;

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbarMessage(`Export complete: ${fileName}`);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddNewSession = (sessionData) => {
    // Get the next session number
    const existingSessionNumbers = (sessions || []).map(s => s.sessionNumber);
    const nextSessionNumber = existingSessionNumbers.length > 0
      ? Math.max(...existingSessionNumbers) + 1
      : 1;

    // Build startDate from date and competitionStart time
    const dateStr = sessionData.startDate;
    const competitionStartTime = sessionData.timeBlocks?.competitionStart;
    let startDate = null;
    if (dateStr && competitionStartTime) {
      // competitionStart might be "YYYY-MM-DD HH:mm:ss", extract time
      const timePart = competitionStartTime.split(' ')[1] || '00:00:00';
      startDate = `${dateStr} ${timePart}`;
    } else if (dateStr) {
      startDate = dateStr;
    } else if (competitionStartTime) {
      startDate = competitionStartTime;
    }

    // Create the new session
    // Ensure numeric fields are numbers, not strings
    const newSession = {
      ...sessionData,
      sessionNumber: parseInt(sessionData.sessionNumber, 10) || nextSessionNumber,
      numFlights: parseInt(sessionData.numFlights, 10) || 1,
      id: crypto.randomUUID(),
      gymnastCount: 0,
      startDate,
    };

    // Add session and sort by startDate then sessionNumber
    const updatedSessions = [...(sessions || []), newSession].sort((a, b) => {
      // First sort by date
      const dateA = a.startDate || a.timeBlocks?.competitionStart || '';
      const dateB = b.startDate || b.timeBlocks?.competitionStart || '';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      // Then by session number
      return (a.sessionNumber || 0) - (b.sessionNumber || 0);
    });

    dispatch(updateCompetition({
      type: 'sessions',
      data: updatedSessions,
      showToast: true,
      toastMessage: `Session ${newSession.sessionNumber} created successfully`
    }));

    setAddSessionDialogOpen(false);
    setHasUnpublishedChanges(true);

    // Navigate to sessions tab and select the new session
    const newIndex = updatedSessions.findIndex(s => s.id === newSession.id);
    setSelectedSessionIndex(newIndex);
    navigateToTab(1, { queryParams: { session: newIndex + 1 } });
  };

  React.useEffect(() => {
    if (snackbarMessage) {
      setSnackbar({ open: true, message: snackbarMessage });
    }
  }, [snackbarMessage]);
  // Update document title when competition changes
  React.useEffect(() => {
    if (competitionWrapper?.competition?.name) {
      document.title = `${competitionWrapper.competition.name} - Virtius NextGen`;
    } else {
      document.title = 'Virtius NextGen';
    }
    
    return () => {
      document.title = 'Virtius NextGen';
    };
  }, [competitionWrapper?.competition?.name]);

  // Show loading screen while fetching competition data
  if (loading && !competitionWrapper?.competition?.id) {
    return (
      <Layout>
        <Box 
          sx={{ 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default'
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
            Loading competition data...
          </Typography>
        </Box>
      </Layout>
    );
  }

  if (!competitionWrapper || !competitionWrapper.competition?.id) {
    return (
      <Layout>
        <Container>
          <Box sx={{ 
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh'
          }}>
            <Typography variant="h4" gutterBottom>Competition not found</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The competition you're looking for doesn't exist or you don't have access to it.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/')}
              size="large"
            >
              Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Layout>
    );
  }

  const formatDateRange = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return 'Not specified';
    
    try {
      // Create timezone-safe dates by treating input as local dates
      const createSafeDate = (dateStr) => {
        if (!dateStr) return null;
        
        // Extract just the date part if it's an ISO string
        const cleanDateStr = String(dateStr).split('T')[0];
        
        // Handle different date formats
        if (cleanDateStr.includes('/')) {
          // MM/DD/YYYY format
          const [month, day, year] = cleanDateStr.split('/').map(Number);
          return new Date(year, month - 1, day); // month is 0-indexed
        } else if (cleanDateStr.includes('-')) {
          // YYYY-MM-DD format - parse as local date to avoid timezone issues
          const [year, month, day] = cleanDateStr.split('-').map(Number);
          return new Date(year, month - 1, day); // month is 0-indexed
        } else {
          // Fallback to native Date parsing
          return new Date(dateStr);
        }
      };
      
      const startDate = createSafeDate(startDateStr);
      const endDate = createSafeDate(endDateStr);
      
      if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return `${startDateStr} - ${endDateStr}`;
      }

      const sameYear = startDate.getFullYear() === endDate.getFullYear();
      
      const startFormat = {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: sameYear ? undefined : 'numeric'
      };
      
      const endFormat = {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      };

      const startFormatted = startDate.toLocaleDateString('en-US', startFormat);
      const endFormatted = endDate.toLocaleDateString('en-US', endFormat);
      
      return `${startFormatted} - ${endFormatted}`;
    } catch (error) {
      console.error('Date parsing error:', error);
      return `${startDateStr} - ${endDateStr}`;
    }
  };



  const handleTabChange = (event, newValue) => {
    navigateToTab(newValue);
  };

  const handleHeroToggle = () => {
    const nextState = !isHeroCollapsed;
    setIsHeroCollapsed(nextState);

    // When expanding hero, smoothly scroll to top only if user is scrolled down
    if (!nextState && window.scrollY > 20) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const showCompactHeader = isScrolled || isHeroCollapsed;
  const stickyTopOffset = showCompactHeader ? 64 : 12;


  return (
    <Layout
      onExport={handleExport}
      onImport={() => setImportModalOpen(true)}
      onAddSession={() => setAddSessionDialogOpen(true)}
      sidebarOpen={sidebarOpen}
      onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
    >
      <Box sx={{ bgcolor: 'background.default' }}>
      {/* Sticky Compact Header - visible when scrolled or hero is collapsed */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 3,
          display: showCompactHeader ? 'block' : 'none',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 64,
            gap: 2
            , position: 'relative'
          }}>
            {/* Left: Menu toggle + Competition name and key info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
              <IconButton
                size="small"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                  '&:hover': {
                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
              {competition.logo && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0
                  }}
                >
                  <img 
                    src={competition.logo}
                    alt="Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {competition.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateRange(competition.startDate, competition.endDate)}
                  </Typography>
                  <Chip
                    label={getStatusLabel(competition.status)}
                    color={getStatusColor(competition.status)}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Center: Stats */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' },
              gap: 3,
              alignItems: 'center'
            }}>
              <CompetitionProgress
                sessions={sessions || []}
                gymnasts={gymnasts}
                gender={sessions?.length > 0
                  ? (sessions.filter(s => s.gender === 'M').length > sessions.filter(s => s.gender === 'F').length ? 'M' : 'F')
                  : 'F'}
                startDate={competition.startDate}
                endDate={competition.endDate}
                size="small"
              />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {competitionWrapper.sessions?.length || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Sessions
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {statistics?.totalTeams || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Clubs
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {(statistics?.totalGymnasts || 0) - (statistics?.totalScratches || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Athletes
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {statistics?.totalJudges || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Judges
                </Typography>
              </Box>
            </Box>

            {/* Right: Quick actions + collapse toggle */}
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center', position: 'relative' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {(hasUnsavedChanges || hasUnpublishedChanges) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                    <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                      {hasUnsavedChanges ? 'Unsaved' : 'Unpublished'}
                    </Typography>
                  </Box>
                )}
                <IconButton
                  onClick={() => {
                    setIsHeroCollapsed(false);
                    setIsEditing(true);
                  }}
                  size="small"
                  sx={{ 
                    color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                    '&:hover': {
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ 
                    color: theme => theme.palette.mode === 'dark' ? '#aaa' : '#666',
                    '&:hover': {
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                    }
                  }}
                  onClick={async () => {
                    // Get fresh competition wrapper from Redux store
                    const state = dispatch((dispatch, getState) => getState());
                    const freshWrapper = state.competition.competitions.find(c => 
                      c?.competition?.id && String(c.competition.id) === String(id)
                    );
                    
                    await dispatch(saveCompetitionVersion(freshWrapper || competitionWrapper));
                    setHasUnsavedChanges(false);
                    setHasUnpublishedChanges(false); // Clear unpublished flag
                    setEditedValues({});
                    setSnackbar({
                      open: true,
                      message: 'Competition saved successfully'
                    });
                    try {
                      const versions = await competitionConfigsService.getVersions(competition.id);
                      const latestVersion = await competitionConfigsService.getLatestVersion(competition.id);
                      setVersionInfo({
                        currentRevision: latestVersion?.revision || 1,
                        totalVersions: versions?.length || 0,
                        lastUpdated: versions?.[0]?.created_at || null
                      });
                    } catch (error) {
                      console.error('Error reloading version info:', error);
                    }
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Container>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: -2 }}>
          <Box
            data-ui="competition-hero-collapse"
            onClick={handleHeroToggle}
            role="button"
            aria-label={isHeroCollapsed ? 'Expand overview' : 'Collapse overview'}
            sx={{
              width: 80,
              height: 12,
              borderRadius: 999,
              backgroundColor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : 'rgba(0,0,0,0.025)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.05)',
              },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 20 10"
              sx={{
                width: 20,
                height: 10,
                fill: 'none',
                stroke: (theme) => theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.65)
                  : alpha(theme.palette.text.primary, 0.65),
                strokeWidth: 2,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                transform: isHeroCollapsed ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <polyline points="1 8 10 4 19 8" />
            </Box>
          </Box>
        </Box>

        {/* Hero Section */}
        <Collapse
          in={!isHeroCollapsed}
          timeout={isDocking ? 0 : 200}
          onExited={() => setIsDocking(false)}
          sx={{ backgroundColor: 'background.default' }}
      >
        <Box
          data-ui="competition-hero"
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            py: 1,
            mt: 2,
            mb: 3,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: `
                linear-gradient(
                  -60deg,
                  transparent 0%,
                  ${competitionColors.primary ? alpha(competitionColors.primary, 0.04) : 'rgba(33, 150, 243, 0.04)'} 35%,
                  transparent 35%
                ),
                linear-gradient(
                  60deg,
                  transparent 10%,
                  ${competitionColors.secondary ? alpha(competitionColors.secondary, 0.04) : 'rgba(245, 0, 87, 0.04)'} 45%,
                  transparent 45%
                )
              `,
              backgroundSize: '240px 240px',
              transform: 'rotate(-12deg)',
              zIndex: 0,
            },
          }}
          ref={heroRef}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 120px',
              gap: 2,
              alignItems: 'flex-start'
            }}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {isEditing ? (
                    <input
                      data-ui="competition-hero-name-input"
                      value={editedValues.name ?? competition.name}
                      onChange={(e) => setEditedValues({ ...editedValues, name: e.target.value })}
                      style={{
                        fontSize: '2rem',
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.23)',
                        borderRadius: '4px',
                        color: 'inherit',
                        padding: '4px 8px',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <Typography data-ui="competition-hero-name" variant="h4" sx={{ mb: 1 }}>
                      {competition.name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(hasUnsavedChanges || hasUnpublishedChanges) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FiberManualRecordIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                        {hasUnsavedChanges ? 'Unsaved' : 'Unpublished'}
                      </Typography>
                    </Box>
                  )}
                  {isEditing ? (
                    <>
                      <Button
                        data-ui="competition-hero-save"
                        startIcon={<CheckIcon />}
                        onClick={() => {
                          let updatedWrapper = { ...competitionWrapper };
                          
                          // Check if start date changed
                          const normalizeDate = (d) => d ? String(d).split(/[T ]/)[0] : '';
                          const oldStartDate = normalizeDate(competition.startDate);
                          const newStartDate = normalizeDate(editedValues.startDate);
                          
                          let datesShifted = false;
                          if (newStartDate && oldStartDate && newStartDate !== oldStartDate) {
                            updatedWrapper = shiftCompetitionDates(updatedWrapper, newStartDate);
                            datesShifted = true;
                          }

                          const updatedCompetition = {
                            ...updatedWrapper,
                            competition: {
                              ...updatedWrapper.competition,
                              ...competition, // Keep original props
                              ...editedValues, // Apply edits
                              colors: {
                                primary: editedValues.primaryColor ?? competitionColors.primary,
                                secondary: editedValues.secondaryColor ?? competitionColors.secondary
                              }
                            }
                          };
                          dispatch(updateCompetition(updatedCompetition));
                          setSnackbar({
                            open: true,
                            message: datesShifted 
                              ? 'Dates and sessions adjusted automatically. Click Save to persist.' 
                              : 'Changes applied locally. Click Save to persist.'
                          });
                          setIsEditing(false);
                          setEditedValues({});
                          setHasUnsavedChanges(false);
                          setHasUnpublishedChanges(true); // Mark as unpublished
                        }}
                        sx={{ mr: 1 }}
                      >
                        Save
                      </Button>
                      <Button
                        data-ui="competition-hero-cancel"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          if (hasUnsavedChanges && !window.confirm('Discard unsaved changes?')) {
                            return;
                          }
                          setIsEditing(false);
                          setEditedValues({});
                          setHasUnsavedChanges(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-ui="competition-hero-edit"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setIsEditing(true);
                        setEditedValues({
                          ...editedValues,
                          primaryColor: competition.colors?.primary ?? competitionColors.primary,
                          secondaryColor: competition.colors?.secondary ?? competitionColors.secondary
                        });
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
              </Box>
              {isEditing ? (
                <input
                  value={editedValues.details ?? competition.details}
                  onChange={(e) => setEditedValues({ ...editedValues, details: e.target.value })}
                  style={{
                    fontSize: '1.25rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.23)',
                    borderRadius: '4px',
                    color: 'inherit',
                    padding: '4px 8px',
                    width: '100%',
                    marginBottom: '16px'
                  }}
                    />
                  ) : (
                    <Typography 
                      data-ui="competition-hero-details"
                      variant="body2" 
                      sx={{ 
                        mb: 1.5, 
                        color: 'text.secondary',
                        fontStyle: 'italic'
                  }}
                >
                  {competition.details}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, mb: 2, flexWrap: 'wrap' }}>
                {versionInfo.currentRevision && (
                  <Chip
                    label={`v${versionInfo.currentRevision}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      height: 22,
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {versionInfo.lastUpdated && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      fontSize: '0.8rem'
                    }}
                  >
                    Last updated: {new Date(versionInfo.lastUpdated + 'Z').toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                )}
                {(hasUnsavedChanges || hasUnpublishedChanges) && (
                  <Chip
                    icon={<FiberManualRecordIcon sx={{ fontSize: 14 }} />}
                    label={hasUnsavedChanges ? "Unsaved Changes" : "Unpublished Changes"}
                    color="warning"
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      height: 22,
                      fontWeight: 'bold',
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.7 }
                      }
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <Box sx={{ minWidth: '200px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">Date Range</Typography>
                    {isEditing && (editedValues.startDate || competition.startDate) && (editedValues.endDate || competition.endDate) && (
                      <Chip 
                        label={`${differenceInDays(
                          new Date(editedValues.endDate || competition.endDate), 
                          new Date(editedValues.startDate || competition.startDate)
                        ) + 1}d`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        inputProps={{ 'data-ui': 'competition-hero-start-date' }}
                        type="date"
                        size="small"
                        value={editedValues.startDate ? editedValues.startDate : (competition.startDate ? String(competition.startDate).split('T')[0] : '')}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          const currentEnd = editedValues.endDate || (competition.endDate ? String(competition.endDate).split('T')[0] : '');
                          
                          const updates = { startDate: newDate };
                          if (newDate && currentEnd && newDate > currentEnd) {
                            updates.endDate = newDate;
                          }
                          setEditedValues({ ...editedValues, ...updates });
                        }}
                        sx={{ width: 140 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>-</Box>
                      <TextField
                        inputProps={{ 'data-ui': 'competition-hero-end-date' }}
                        type="date"
                        size="small"
                        value={editedValues.endDate ? editedValues.endDate : (competition.endDate ? String(competition.endDate).split('T')[0] : '')}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          const currentStart = editedValues.startDate || (competition.startDate ? String(competition.startDate).split('T')[0] : '');
                          
                          const updates = { endDate: newDate };
                          if (newDate && currentStart && newDate < currentStart) {
                            updates.startDate = newDate;
                          }
                          setEditedValues({ ...editedValues, ...updates });
                        }}
                        sx={{ width: 140 }}
                      />
                    </Box>
                  ) : (
                    <Typography data-ui="competition-hero-date-range" variant="body1">{formatDateRange(competition.startDate, competition.endDate)}</Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Time Zone</Typography>
                  {isEditing ? (
                    <TextField
                      select
                      size="small"
                      inputProps={{ 'data-ui': 'competition-hero-timezone' }}
                      value={editedValues.timezone ?? competition.timezone ?? 'America/New_York'}
                      onChange={(e) => setEditedValues({ ...editedValues, timezone: e.target.value })}
                      sx={{ minWidth: 180 }}
                    >
                      {TIMEZONES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography data-ui="competition-hero-timezone" variant="body1">
                      {getTimezoneLabel(competition.timezone)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <input
                        data-ui="competition-hero-facility"
                        value={editedValues.facility ?? competition.facility ?? ''}
                        onChange={(e) => setEditedValues({ ...editedValues, facility: e.target.value })}
                        placeholder="Facility"
                        style={{
                          background: 'transparent',
                          border: '1px solid rgba(255, 255, 255, 0.23)',
                          borderRadius: '4px',
                          color: 'inherit',
                          padding: '4px 8px',
                          width: '100%'
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography data-ui="competition-hero-location" variant="body1">
                      {competition.facility ?
                        competition.facility
                        : 'Not specified'}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Director</Typography>
                  {isEditing ? (
                    <input
                      data-ui="competition-hero-director"
                      value={editedValues.director ?? competition.director ?? ''}
                      onChange={(e) => setEditedValues({ ...editedValues, director: e.target.value })}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.23)',
                        borderRadius: '4px',
                        color: 'inherit',
                        padding: '4px 8px',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <Typography data-ui="competition-hero-director" variant="body1">{competition.director || 'Not specified'}</Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Organization</Typography>
                  {isEditing ? (
                    <input
                      data-ui="competition-hero-organization"
                      value={editedValues.organization ?? competition.organization ?? ''}
                      onChange={(e) => setEditedValues({ ...editedValues, organization: e.target.value })}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.23)',
                        borderRadius: '4px',
                        color: 'inherit',
                        padding: '4px 8px',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <Typography data-ui="competition-hero-organization" variant="body1">{competition.organization || 'Not specified'}</Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Sanction</Typography>
                  {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        select
                        size="small"
                        inputProps={{ 'data-ui': 'competition-hero-sanction-org' }}
                        value={editedValues.sanctionOrg ?? competition.sanctionOrg ?? ''}
                        onChange={(e) => setEditedValues({ ...editedValues, sanctionOrg: e.target.value })}
                        sx={{ minWidth: 100 }}
                      >
                        {SANCTION_ORGS.map((org) => (
                          <MenuItem key={org.value} value={org.value}>
                            {org.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        inputProps={{ 'data-ui': 'competition-hero-sanction-id' }}
                        value={editedValues.sanctionId ?? competition.sanctionId ?? ''}
                        onChange={(e) => setEditedValues({ ...editedValues, sanctionId: e.target.value })}
                        placeholder="ID"
                        sx={{ width: 100 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1">
                      {competition.sanctionOrg && competition.sanctionId
                        ? `${competition.sanctionOrg} ${competition.sanctionId}`
                        : (competition.sanctionOrg || competition.sanctionId || 'Not specified')}
                    </Typography>
                  )}
                  {!isEditing && competitionWrapper?.metadata?.usagLastModified && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      Synced: {new Date(competitionWrapper.metadata.usagLastModified).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: '200px', maxWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Levels</Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    { levels.map(level => level.id).sort().join(', ') || 'Not specified'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  {isEditing ? (
                    <TextField
                      select
                      size="small"
                      inputProps={{ 'data-ui': 'competition-hero-status' }}
                      value={editedValues.status ?? competition.status ?? 'draft'}
                      onChange={(e) => setEditedValues({ ...editedValues, status: e.target.value })}
                      sx={{ minWidth: 150 }}
                    >
                      {Object.values(COMPETITION_STATUSES).map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Chip 
                      label={getStatusLabel(competition.status)}
                      color={getStatusColor(competition.status)}
                      size="small"
                    />
                  )}
                </Box>
                {/* <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Registration Deadline</Typography>
                  <Typography variant="body1">{competition.registrationDeadline || 'Not specified'}</Typography>
                </Box>
                 */}
                <Box sx={{ minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                  {isEditing ? (
                    <input
                      data-ui="competition-hero-website"
                      value={editedValues.website ?? competition.website ?? ''}
                      onChange={(e) => setEditedValues({ ...editedValues, website: e.target.value })}
                      placeholder="https://..."
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.23)',
                        borderRadius: '4px',
                        color: 'inherit',
                        padding: '4px 8px',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {competition.website ? (
                        <a 
                          href={competition.website.startsWith('http') ? competition.website : `https://${competition.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                        >
                          {competition.website}
                        </a>
                      ) : 'Not specified'}
                    </Typography>
                  )}
                </Box>
              </Box>


              {/* Statistics Summary */}
              <Box sx={{
                mt: 1.5,
                pt: 1,
                borderTop: 1,
                borderColor: 'rgba(255, 255, 255, 0.1)',
              }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 1,
                  alignItems: 'center',
                  textAlign: 'center'
                }}>
                  <Box>
                    <CompetitionProgress
                      sessions={sessions || []}
                      gymnasts={gymnasts}
                      gender={sessions?.length > 0
                        ? (sessions.filter(s => s.gender === 'M').length > sessions.filter(s => s.gender === 'F').length ? 'M' : 'F')
                        : 'F'}
                      startDate={competition.startDate}
                      endDate={competition.endDate}
                    />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {competitionWrapper.sessions?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sessions
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {statistics?.totalTeams || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Clubs
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {(statistics?.totalGymnasts || 0) - (statistics?.totalScratches || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Athletes
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.5 }}>
                      {statistics?.totalJudges || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Judges
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              <input
                id="logo-upload-input"
                type="file"
                data-ui="competition-hero-logo-input"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Box
                data-ui="competition-hero-logo"
                onClick={(e) => {
                  // console.log('Logo box clicked', { isEditing, fileInputRef: fileInputRef.current });
                  if (isEditing && fileInputRef.current) {
                    // Prevent event bubbling
                    e.stopPropagation();
                    // Try to trigger click
                    try {
                      fileInputRef.current.click();
                      // console.log('Click triggered successfully');
                    } catch (error) {
                      console.error('Error triggering click:', error);
                    }
                  }
                }}
                onDragOver={(e) => {
                  if (isEditing) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onDrop={(e) => {
                  if (isEditing) {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      const event = { target: { files: [file] } };
                      handleLogoUpload(event);
                    }
                  }
                }}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: isEditing ? 'pointer' : 'default',
                  position: 'relative',
                  ...(isEditing && {
                    '&:hover, &:active': {
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                      }
                    }
                  })
                }}
              >
                {uploadingLogo ? (
                  <CircularProgress size={24} />
                ) : competition.logo ? (
                  <img 
                    src={competition.logo}
                    alt={`${competition.name} logo`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : !isEditing ? (
                  <Typography variant="body2" color="text.secondary">
                    Logo Placeholder
                  </Typography>
                ) : (
                  <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.75rem' }}>
                      Upload logo or drag image here
                    </Typography>
                    <label htmlFor="logo-upload-input" style={{ cursor: 'pointer' }}>
                      <Button 
                        size="small" 
                        variant="outlined"
                        component="span"
                        sx={{ fontSize: '0.7rem', py: 0.5 }}
                      >
                        Choose File
                      </Button>
                    </label>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                {isEditing && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Theme Colors</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <input
                        type="color"
                        data-ui="competition-hero-primary-color"
                        value={editedValues.primaryColor ?? competitionColors.primary ?? '#2196f3'}
                        onChange={(e) => setEditedValues({
                          ...editedValues,
                          primaryColor: e.target.value
                        })}
                        style={{
                          width: '70px',
                          height: '30px',
                          padding: '0',
                          border: '1px solid rgba(255, 255, 255, 0.23)',
                          borderRadius: '4px',
                          background: 'transparent'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">Primary</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <input
                        type="color"
                        data-ui="competition-hero-secondary-color"
                        value={editedValues.secondaryColor ?? competitionColors.secondary ?? '#f50057'}
                        onChange={(e) => setEditedValues({
                          ...editedValues,
                          secondaryColor: e.target.value
                        })}
                        style={{
                          width: '70px',
                          height: '30px',
                          padding: '0',
                          border: '1px solid rgba(255, 255, 255, 0.23)',
                          borderRadius: '4px',
                          background: 'transparent'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">Secondary</Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Button
                    data-ui="competition-hero-import"
                    variant="contained"
                    startIcon={<FileUploadIcon />}
                    fullWidth
                    size="small"
                    sx={{
                      width: 120,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
                      color: theme => theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                      },
                      fontSize: '0.8rem',
                      boxShadow: 1
                    }}
                    onClick={() => setImportModalOpen(true)}
                  >
                    Import
                  </Button>
                  <Button
                    data-ui="competition-hero-export"
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    fullWidth
                    size="small"
                    sx={{
                      width: 120,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
                      color: theme => theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                      },
                      fontSize: '0.8rem',
                      boxShadow: 1
                    }}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  <Button
                    data-ui="competition-hero-save-button"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    fullWidth
                    size="small"
                    sx={{
                      width: 120,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
                      color: theme => theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                      },
                      fontSize: '0.8rem',
                      ...(hasUnsavedChanges && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.05)' }
                        }
                      })
                    }}
                    onClick={async () => {
                      await dispatch(saveCompetitionVersion(competitionWrapper));
                      // Clear state flags
                      setHasUnsavedChanges(false);
                      setHasUnpublishedChanges(false);
                      setEditedValues({});
                      setSnackbar({
                        open: true,
                        message: 'Competition saved successfully'
                      });
                      
                      // Reload version info after save
                      try {
                        const versions = await competitionConfigsService.getVersions(competition.id);
                        const latestVersion = await competitionConfigsService.getLatestVersion(competition.id);
                        setVersionInfo({
                          currentRevision: latestVersion?.revision || 1,
                          totalVersions: versions?.length || 0,
                          lastUpdated: versions?.[0]?.created_at || null
                        });
                      } catch (error) {
                        console.error('Error reloading version info:', error);
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    data-ui="competition-hero-history"
                    variant="contained"
                    startIcon={<HistoryIcon />}
                    fullWidth
                    size="small"
                    sx={{
                      width: 120,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
                      color: theme => theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                      },
                      fontSize: '0.8rem',
                      boxShadow: 1
                    }}
                    onClick={() => setHistoryDialogOpen(true)}
                  >
                    {versionInfo.totalVersions > 0 
                      ? `History (${versionInfo.totalVersions})`
                      : 'History'
                    }
                  </Button>
                  <Button
                    data-ui="competition-hero-compare"
                    variant="contained"
                    startIcon={<CompareArrowsIcon />}
                    fullWidth
                    size="small"
                    sx={{
                      width: 120,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
                      color: theme => theme.palette.text.primary,
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
                      },
                      fontSize: '0.8rem',
                      boxShadow: 1
                    }}
                    onClick={() => setCompareDialogOpen(true)}
                    disabled={versionInfo.totalVersions < 2}
                  >
                    Compare
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
        </Box>
      </Collapse>

      {/* Main Content */}
      <Container maxWidth={layoutWidth === 'constrained' ? 'lg' : false} sx={{ position: 'relative', py: 0 }}>
        <Box
          sx={{
            position: 'sticky',
            top: stickyTopOffset, // Stick just below compact header or near top when expanded
            zIndex: 1000,
            backgroundColor: 'background.default',
            pt: 2,
            pb: 1.5,
          }}
        >
        <Paper
          sx={{
            boxShadow: isScrolled ? 3 : 1,
            backgroundColor: 'background.paper',
            transition: 'box-shadow 0.3s ease-in-out',
          }}
          data-ui="competition-tabs-container"
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="fullWidth"
            data-ui="competition-tabs"
            sx={{
              '& .MuiTabs-indicator': {
                // Keep theme color for branding, make it thicker for visibility
                backgroundColor: competitionColors.primary,
                height: 3,
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  // In dark mode, use white text for better contrast against dark backgrounds
                  // In light mode, use the competition's primary color
                  color: theme => theme.palette.mode === 'dark'
                    ? '#fff'
                    : competitionColors.primary,
                  // Add subtle theme-colored background in dark mode for visual emphasis
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? alpha(competitionColors.primary, 0.15)
                    : 'transparent',
                  borderRadius: 1,
                },
              },
            }}
          >
            <Tab icon={<CalendarMonthIcon />} label="Schedule" data-ui="tab-schedule" />
            <Tab icon={<PeopleAltIcon />} label="Sessions & Squads" data-ui="tab-sessions" />
            <Tab icon={<GroupsIcon />} label="Athletes & Teams" data-ui="tab-athletes-teams" />
            <Tab icon={<GavelIcon />} label="Judges & Scoring" data-ui="tab-judges" />
            <Tab icon={<EqualizerIcon />} label="Meet Statistics" data-ui="tab-statistics" />
            <Tab icon={<MapIcon />} label="Venue" data-ui="tab-venue" />
            <Tab icon={<EmojiEventsIcon />} label="Results" data-ui="tab-results" />
          </Tabs>
        </Paper>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ pt: 2, pb: 0 }}>
        <TabPanel value={currentTab} index={0} data-ui="schedule-tab-panel">
          <CompetitionSchedule
            sessions={sessions || []}
            gymnasts={gymnasts || []}
            competitionColors={competitionColors}
            startDate={competition.startDate}
            endDate={competition.endDate}
            competitionName={competition.name}
            timezone={competition.timezone}
            rotations={competitionWrapper.rotations || []}
            onSessionClick={(session) => {
              const index = (sessions || []).findIndex(s => String(s.sessionNumber) === String(session.sessionNumber));

              if (index !== -1) {
                setSelectedSessionIndex(index);
                // URL uses 1-based session numbers for human readability
                navigateToTab(1, { queryParams: { session: index + 1 } });
              }
            }}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1} data-ui="sessions-tab-panel">
          <SessionDetails
            session={{
              ...sessions?.[selectedSessionIndex],
              sessionsByDay: (() => {
                // Normalize date to YYYY-MM-DD, handling various formats
                const normalizeDate = (dateStr) => {
                  if (!dateStr) return '';
                  const str = String(dateStr).trim();

                  // Try YYYY-MM-DD format first
                  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
                  if (isoMatch) return isoMatch[1];

                  // Try MM/DD/YYYY format
                  const usMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
                  if (usMatch) return `${usMatch[3]}-${usMatch[1]}-${usMatch[2]}`;

                  return '';
                };

                // Group sessions by normalized date
                const grouped = sessions?.reduce((acc, s) => {
                  const sessionDate = normalizeDate(s.startDate) ||
                                     normalizeDate(s.timeBlocks?.competitionStart);
                  if (sessionDate && !acc[sessionDate]) {
                    acc[sessionDate] = [];
                  }
                  if (sessionDate) {
                    acc[sessionDate].push(s.sessionNumber);
                  }
                  return acc;
                }, {}) || {};
                // Sort by date and return as ordered object
                return Object.keys(grouped)
                  .sort()
                  .reduce((acc, key) => {
                    acc[key] = grouped[key].sort((a, b) => a - b);
                    return acc;
                  }, {});
              })()
            } || {}}
            competitionColors={competitionColors}
            onNextSession={() => {
              if (selectedSessionIndex < (sessions?.length || 0) - 1) {
                const newIndex = selectedSessionIndex + 1;
                setSelectedSessionIndex(newIndex);
                updateQueryParams({ session: newIndex + 1 });
              }
            }}
            onPrevSession={() => {
              if (selectedSessionIndex > 0) {
                const newIndex = selectedSessionIndex - 1;
                setSelectedSessionIndex(newIndex);
                updateQueryParams({ session: newIndex + 1 });
              }
            }}
            hasNextSession={selectedSessionIndex < (sessions?.length || 0) - 1}
            hasPrevSession={selectedSessionIndex > 0}
            onDayClick={(date) => {
              const firstSessionIndex = sessions?.findIndex(s =>
                s.startDate?.split(' ')[0] === date ||
                s.timeBlocks?.competitionStart?.split(' ')[0] === date
              );
              if (firstSessionIndex !== -1) {
                setSelectedSessionIndex(firstSessionIndex);
                updateQueryParams({ session: firstSessionIndex + 1 });
              }
            }}
            onSessionClick={(session) => {
              const index = (sessions || []).findIndex(s => String(s.sessionNumber) === String(session));
              if (index !== -1) {
                setSelectedSessionIndex(index);
                // URL uses 1-based session numbers for human readability
                navigateToTab(1, { queryParams: { session: index + 1 } });
              }
            }}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2} data-ui="athletes-teams-tab-panel">
          <Box
            sx={{
              mb: 2,
              pb: 0.75,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap'
            }}
            data-ui="athletes-teams-tab-header"
          >
            <Typography variant="h6">
              Athletes & Teams Management
            </Typography>
            <ToggleButtonGroup
              value={athleteTeamsView}
              exclusive
              onChange={(event, newView) => {
                if (newView) {
                  setAthleteTeamsView(newView);
                  updateQueryParams({ view: newView });
                }
              }}
              aria-label="athletes teams view"
              size="small"
              data-ui="athletes-teams-view-toggle"
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.25,
                  px: 1.25,
                  fontSize: '0.85rem'
                },
                '& .MuiToggleButton-root + .MuiToggleButton-root': {
                  ml: 0
                }
              }}
            >
              <ToggleButton value="athletes" aria-label="athletes view" data-ui="athletes-teams-view-athletes">
                <PersonIcon sx={{ mr: 1 }} />
                Athletes
              </ToggleButton>
              <ToggleButton value="coaches" aria-label="coaches view" data-ui="athletes-teams-view-coaches">
                <PeopleAltIcon sx={{ mr: 1 }} />
                Coaches
              </ToggleButton>
              <ToggleButton value="teams" aria-label="teams view" data-ui="athletes-teams-view-teams">
                <GroupsIcon sx={{ mr: 1 }} />
                Teams
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <React.Suspense fallback={
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Skeleton variant="rectangular" width={300} height={40} />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" width={70} height={32} />
                  <Skeleton variant="rectangular" width={70} height={32} />
                  <Skeleton variant="rectangular" width={70} height={32} />
                </Box>
              </Box>
              <Skeleton variant="rectangular" height={400} />
            </Box>
          }>
            <CompetitionTeams
              teams={teams || []}
              athletes={gymnasts || []}
              coaches={coaches || []}
              competitionColors={competitionColors}
              view={athleteTeamsView}
              onViewChange={(newView) => {
                setAthleteTeamsView(newView);
                updateQueryParams({ view: newView });
              }}
            />
          </React.Suspense>
        </TabPanel>

        <TabPanel value={currentTab} index={3} data-ui="judges-tab-panel">
          <Box
            sx={{
              mb: 2,
              pb: 0.75,
              borderBottom: 1,
              borderColor: 'divider'
            }}
            data-ui="judges-tab-header"
          >
            <Typography variant="h6">
              Judges & Scoring Configuration
            </Typography>
          </Box>
          <CompetitionJudging
            judges={competition.judges || []}
            scoringRules={competition.scoringRules || []}
            levels={levels || []}
            sessions={sessions || []}
            competitionColors={competitionColors}
            gender={(sessions || []).length > 0
              ? (sessions.filter(s => s.gender === 'M').length > sessions.filter(s => s.gender === 'F').length ? 'M' : 'F')
              : 'F'}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={4} data-ui="statistics-tab-panel">
          <Box
            sx={{
              mb: 2,
              pb: 0.75,
              borderBottom: 1,
              borderColor: 'divider'
            }}
            data-ui="statistics-tab-header"
          >
            <Typography variant="h6">
              Meet Statistics
            </Typography>
          </Box>
          <CompetitionStats
            gymnasts={gymnasts || []}
            teams={teams || []}
            sessions={sessions || []}
            competitionColors={competitionColors}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={5} data-ui="venue-tab-panel">
          <Box
            sx={{
              mb: 2,
              pb: 0.75,
              borderBottom: 1,
              borderColor: 'divider'
            }}
            data-ui="venue-tab-header"
          >
            <Typography variant="h6">
              Venue
            </Typography>
          </Box>
          <FacilityLayoutDesigner
            initialItems={competition.venueLayout || []}
            onLayoutChange={(newLayout) => {
              const currentLayout = competition.venueLayout || [];
              // Simple check to avoid setting dirty state on initial load if empty
              if (JSON.stringify(newLayout) !== JSON.stringify(currentLayout)) {
                setEditedValues(prev => ({ ...prev, venueLayout: newLayout }));
                setHasUnpublishedChanges(true);
              }
            }}
            onSave={() => {
              // Save locally to Redux - user must click main Save button to persist to DB
              const updatedWrapper = {
                ...competitionWrapper,
                competition: {
                  ...competition,
                  venueLayout: editedValues.venueLayout || competition.venueLayout
                }
              };
              dispatch(updateCompetition(updatedWrapper));
              setHasUnpublishedChanges(true);
            }}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={6} data-ui="results-tab-panel">
          <React.Suspense fallback={
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Skeleton variant="rectangular" width={150} height={32} />
                <Skeleton variant="rectangular" width={200} height={32} />
                <Box sx={{ ml: 'auto' }}>
                  <Skeleton variant="rectangular" width={280} height={40} />
                </Box>
              </Box>
              <Skeleton variant="rectangular" height={400} />
            </Box>
          }>
            <CompetitionResults
              athletes={gymnasts || []}
              sessions={sessions || []}
              teams={teams || []}
              ageGroups={ageGroups || []}
              competitionColors={competitionColors}
              gender={(sessions || []).length > 0
                ? (sessions.filter(s => s.gender === 'M').length > sessions.filter(s => s.gender === 'F').length ? 'M' : 'F')
                : 'F'}
              setSnackbar={setSnackbar}
            />
          </React.Suspense>
        </TabPanel>
        </Box>
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={(importedData, overwrite) => {
          handleImport(importedData, setSnackbarMessage, overwrite);
        }}
        onFileSelect={() => { }}
        currentCompetition={competitionWrapper}
      />
        <VersionHistoryDialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          competitionId={competition?.id}
          onLoadVersion={async (version) => {
            await dispatch(loadCompetitionVersion(competition.id, version.revision));
            setHistoryDialogOpen(false);
            // Update version info
            try {
              const versions = await competitionConfigsService.getVersions(competition.id);
              const latestVersion = await competitionConfigsService.getLatestVersion(competition.id);
              setVersionInfo({
                currentRevision: version.revision,
                totalVersions: versions?.length || 0,
                lastUpdated: version.created_at || null
              });
            } catch (error) {
              console.error('Error reloading version info:', error);
            }
          }}
        />
        <RevisionCompareDialog
          open={compareDialogOpen}
          onClose={() => setCompareDialogOpen(false)}
          competitionId={competition?.id}
        />
        {/* Add Session Dialog */}
        <Dialog
          open={addSessionDialogOpen}
          onClose={() => setAddSessionDialogOpen(false)}
          maxWidth="md"
          fullWidth
          data-ui="dialog-add-session"
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Add New Session
            <IconButton
              onClick={() => setAddSessionDialogOpen(false)}
              size="small"
              data-ui="btn-close-add-session"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <SessionDefinition
              initialData={{
                sessionNumber: (sessions || []).length > 0
                  ? Math.max(...(sessions || []).map(s => s.sessionNumber)) + 1
                  : 1,
                gender: (sessions || []).length > 0
                  ? (sessions.filter(s => s.gender === 'M').length > sessions.filter(s => s.gender === 'F').length ? 'M' : 'F')
                  : 'F',
                type: 'Traditional',
                numFlights: 1,
                levels: [],
                timeBlocks: {},
                enabledTimeBlocks: ['openStretchStart', 'warmUpStart', 'marchInStart', 'competitionStart', 'competitionEnd', 'awardsEnd']
              }}
              availableRotations={rotations || []}
              onSave={handleAddNewSession}
              onCancel={() => setAddSessionDialogOpen(false)}
              editMode={false}
              sessionDate={competition.startDate}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
};

const TabPanel = React.memo(function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
});

export default CompetitionDetails;
